import React, { useEffect, useState } from "react";
import { ArrowLeft } from "lucide-react";
import { useParams, Link } from "react-router-dom";
import { AdminPageLayout } from "@/layouts/AdminPageLayout";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { AutoSaveForm } from "@/components/forms/AutoSaveForm";
import { useAppToast } from "@/hooks/useAppToast";
import { getLevelTestGroup, updateLevelTestGroup } from "@/services/admin/levelTestGroups.service";
import { LevelTestGroup, UpdateLevelTestGroupInput } from "@/services/admin/levelTestGroups.interfaces";
import { Card } from "@/components/ui/card";
import { useLoading } from "@/lib/LoadingHook";
import { LevelTestGroupExerciseProvider } from "./LevelTestGroupExerciseContext";
import { LevelTestGroupExercises } from "./LevelTestGroupExercises";

interface LevelTestGroupInfoFormProps {
  groupData: LevelTestGroup;
  onSave: (data: UpdateLevelTestGroupInput) => Promise<void>;
}

const LevelTestGroupInfoForm: React.FC<LevelTestGroupInfoFormProps> = ({
  groupData,
  onSave,
}) => {
  return (
    <div className="mb-8">
      <AutoSaveForm onSave={onSave} className="space-y-4">
        <Card className="p-6 space-y-6">
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Nome do grupo</Label>
              <Input id="name" name="name" type="text" defaultValue={groupData.name} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="description">Descrição</Label>
              <Textarea id="description" name="description" className="h-32" defaultValue={groupData.description} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="level">Nível</Label>
              <Input id="level" name="level" type="number" defaultValue={groupData.level} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="status">Status</Label>
              <select id="status" name="status" className="w-full rounded-md border border-input bg-background px-3 py-2" defaultValue={groupData.status}>
                <option value="Draft">Rascunho</option>
                <option value="Published">Publicado</option>
                <option value="Archived">Arquivado</option>
              </select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="skillArea">Área de habilidade (opcional)</Label>
              <Input
                id="skillArea"
                name="skillArea"
                type="text"
                defaultValue={groupData.skillArea || ""}
                placeholder="Ex.: Gramática, Vocabulário, Compreensão"
              />
            </div>
          </div>
        </Card>
      </AutoSaveForm>
    </div>
  );
};

const AdminEditLevelTestGroupPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const toast = useAppToast();
  const { isLoading, whenLoading } = useLoading();
  const [groupData, setGroupData] = useState<LevelTestGroup | null>(null);

  useEffect(() => {
    const loadGroup = async () => {
      if (!id) return;

      await whenLoading(async () => {
        try {
          const data = await getLevelTestGroup(id);
          setGroupData(data);
        } catch (error) {
          toast.error("Falha ao carregar dados do grupo de teste de nivelamento");
          console.error("Error loading level test group:", error);
        }
      });
    };

    loadGroup();
  }, [id]);

  const handleSave = async (changes: UpdateLevelTestGroupInput) => {
    if (!id) return;

    try {
      await updateLevelTestGroup(id, changes);
      setGroupData((prev) => (prev ? { ...prev, ...changes } : null));
      toast.success("Alterações salvas!");
    } catch (error) {
      toast.error("Erro ao salvar alterações.");
      console.error("Error updating level test group:", error);
      throw error;
    }
  };

  if (isLoading) {
    return <AdminPageLayout title="Carregando..."><div className="flex items-center justify-center py-8">Carregando...</div></AdminPageLayout>;
  }

  if (!groupData) {
    return <AdminPageLayout title="Grupo não encontrado"><div className="flex items-center justify-center py-8">Grupo de teste de nivelamento não encontrado.</div></AdminPageLayout>;
  }

  const BackButtons = () => (
    <div className="flex gap-4 mb-4">
      <Link to="/admin/educational/level-tests-management" className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900">
        <ArrowLeft className="w-4 h-4" />
        Voltar aos grupos de teste de nivelamento
      </Link>
    </div>
  );

  return (
    <AdminPageLayout title={`Editar ${groupData.name}`}>
      <BackButtons />
      <main id="main-content" className="mx-auto py-8 space-y-8">
        <LevelTestGroupInfoForm groupData={groupData} onSave={handleSave} />
        
        <LevelTestGroupExerciseProvider levelTestGroupId={id || ""}>
          <LevelTestGroupExercises />
        </LevelTestGroupExerciseProvider>
      </main>
    </AdminPageLayout>
  );
};

export default AdminEditLevelTestGroupPage;
